import {css, StyleSheet} from 'aphrodite';
import React, {RefObject, useRef, useMemo} from 'react';

import {AccessibleGridCell, isAccessibleGridCell, FullyAccessibleGrid} from 'constants/SampleConstants';
import {usePlateMapFromGrid} from 'samples/grids/hooks/usePlateMapFromGrid';
import PlateMapDisplayLayerDropdown from 'samples/plate-maps/display-layer-controls/PlateMapDisplayLayerDropdown';
import usePlateMapDisplayAttributeState from 'samples/plate-maps/hooks/usePlateMapDisplayAttributeState';
import {
  REAGENTS_ATTRIBUTE_SENTINEL,
  WELL_ROLE_ATTRIBUTE_SENTINEL,
  ReagentAssignmentGroup,
} from 'samples/plate-maps/PlateMapConstants';
import PlateMapLegend from 'samples/plate-maps/PlateMapLegend';
import {
  getDisplayLayerAttributeKey,
  getWellPositionFromIndex,
  getContentConcentrationOptions,
} from 'samples/plate-maps/PlateMapUtil';
import PlateMapView from 'samples/plate-maps/PlateMapView';
import {getCellsAsIndices} from 'samples/SampleUtil';

interface GridVisualizationProps {
  grid: FullyAccessibleGrid;
  selectedCells: Array<AccessibleGridCell>;
  onSelectCells: (selectedCells: Array<AccessibleGridCell>) => void;
  deselectWellsOnClickRefs?: Array<RefObject<HTMLElement>>;
}

/**
 * Displays an existing grid entity as a plate map and renders different layers of information
 * about the plate map.
 */
export default function GridVisualization({
  grid,
  selectedCells,
  onSelectCells,
  deselectWellsOnClickRefs = [],
}: GridVisualizationProps): JSX.Element {
  const {plateMap} = usePlateMapFromGrid(grid);
  const {assignmentGroupsPerAttribute, selectedAttribute, setSelectedAttribute, displayLayer} =
    usePlateMapDisplayAttributeState(plateMap, REAGENTS_ATTRIBUTE_SENTINEL, true);
  const containerRef = useRef<HTMLDivElement>(null);

  // Create measurement layer options for content concentration visualization
  const measurementLayersOptions = useMemo(() => {
    const reagentGroups = assignmentGroupsPerAttribute[REAGENTS_ATTRIBUTE_SENTINEL] ?? {};
    return getContentConcentrationOptions(reagentGroups as {[id: string]: ReagentAssignmentGroup});
  }, [assignmentGroupsPerAttribute]);

  return (
    <div ref={containerRef}>
      <div className={css(styles.layerDropdown)}>
        <PlateMapDisplayLayerDropdown
          containerSchemaId={grid.storableSchema.containerSchemaId}
          additionalAttributeOptions={[REAGENTS_ATTRIBUTE_SENTINEL, WELL_ROLE_ATTRIBUTE_SENTINEL]}
          selectedAttribute={selectedAttribute}
          setSelectedAttribute={setSelectedAttribute}
          measurementLayersOptions={measurementLayersOptions}
        />
      </div>
      <PlateMapView
        plateMap={plateMap}
        selectedWellIndices={new Set(getCellsAsIndices(selectedCells, grid))}
        setSelectedWellIndices={(indices: Set<number>): void => {
          const newSelectedCells = [...indices]
            .map((index) => {
              const {row, column} = getWellPositionFromIndex(index, plateMap.size);
              return grid.cells[row][column];
            })
            .filter((cell): cell is AccessibleGridCell => isAccessibleGridCell(cell));
          onSelectCells(newSelectedCells);
        }}
        assignmentGroupsById={
          assignmentGroupsPerAttribute[getDisplayLayerAttributeKey(selectedAttribute)] ?? {}
        }
        isViewOnly={false}
        displayLayer={displayLayer}
        previewedWellIndices={new Set()}
        attribute={selectedAttribute}
        deselectWellsOnClickRefs={[containerRef, ...deselectWellsOnClickRefs]}
        showFinalMeasurements={false}
      />
      <PlateMapLegend
        plateMap={plateMap}
        displayLayer={displayLayer}
        assignmentGroupsById={
          assignmentGroupsPerAttribute[getDisplayLayerAttributeKey(selectedAttribute)] ?? {}
        }
        attribute={selectedAttribute}
        groupedValidationErrors={{}}
      />
    </div>
  );
}

const styles = StyleSheet.create({
  layerDropdown: {
    marginBottom: '16px',
    width: '300px',
  },
});
